<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Daily Invoice Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyInvoiceValueFilter"
              @reset="resetInvoiceValueFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingInvoiceValueFilter.startDate"
                  :endDate="editingInvoiceValueFilter.endDate"
                  @updateStartDate="editingInvoiceValueFilter.startDate = $event"
                  @updateEndDate="editingInvoiceValueFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedInvoiceValueFilter.startDate | formatDate}} - {{appliedInvoiceValueFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Invoice Value" highlight>
              {{formatCurrency(invoiceSummaryData.invoiceTotalValue)}}
              <template v-slot:info>Over {{invoiceSummaryData.invoiceTotalCount}} invoice{{displayCountPlural(invoiceSummaryData.invoiceTotalCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Daily Value">
              {{formatCurrency(invoiceSummaryData.averageInvoiceValue)}}
              <template v-slot:info>Over {{invoiceSummaryData.invoiceTotalCount}} invoice{{displayCountPlural(invoiceSummaryData.invoiceTotalCount)}} during the period containing {{invoiceSummaryData.activeInvoiceDayCount}} working day{{displayCountPlural(invoiceSummaryData.activeInvoiceDayCount)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow v-if="invoiceSummaryData.largestInvoiceInfo">
          <div class="col">
            <AnalyticsDashboardValue title="Largest Invoice">
              {{formatCurrency(invoiceSummaryData.largestInvoiceInfo.totalPrice)}}
              <template v-slot:info>Invoice <LinkInvoice :id="invoiceSummaryData.largestInvoiceInfo.id" :name="invoiceSummaryData.largestInvoiceInfo.sequenceNumber"></LinkInvoice> for {{invoiceSummaryData.largestInvoiceInfo.contact.name}} on {{formatDate(invoiceSummaryData.largestInvoiceInfo.dateInvoiceCreated)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Daily Invoice Value"
          :series="invoiceDailyValueData.series"
          :xAxis="invoiceDailyValueData.xAxis"
          :yAxis="invoiceDailyValueData.yAxis"
          :legend="invoiceDailyValueData.legend"
          :options="invoiceDailyValueData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'InvoiceDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      invoiceSummaryData: {
        invoiceTotalValue: 0,
        invoiceTotalCount: 0,
        averageInvoiceValue: 0,
        largestInvoiceInfo: null,
        activeInvoiceDayCount: 0
      },
      invoiceDailyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultInvoiceValueFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingInvoiceValueFilter: {},
      appliedInvoiceValueFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.editingInvoiceValueFilter = _.clone(this.defaultInvoiceValueFilter)
    this.appliedInvoiceValueFilter = _.clone(this.defaultInvoiceValueFilter)

    this.requestChartData()

    this.subscribeEndpoint('/accounts/invoices', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/accounts/invoices')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyInvoiceValueFilter() {
      this.appliedInvoiceValueFilter = _.clone(this.editingInvoiceValueFilter)

      this.requestChartData()
    },
    resetInvoiceValueFilter() {
      this.editingInvoiceValueFilter = _.clone(this.defaultInvoiceValueFilter)
      this.appliedInvoiceValueFilter = _.clone(this.defaultInvoiceValueFilter)

      this.requestChartData()
    },
    requestChartData() {
      var invoiceValueFilter = {
        type: 'daily',
        startDate: this.appliedInvoiceValueFilter.startDate,
        endDate: this.appliedInvoiceValueFilter.endDate,
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      Promise.all([
        this.$apiService.analyticsService.getInvoiceValueChartData(invoiceValueFilter),
        this.$apiService.analyticsService.getInvoiceValueSummary(invoiceValueFilter)
      ])
      .then(results => {
        let chartData = results[0].data
        let invoiceSummaryData = results[1].data

        this.invoiceDailyValueData.xAxis = {
          exportName: 'Date',
          exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
          data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM'))
        }
        this.invoiceDailyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatCurrencySuffix(value, 1);
              }
            }
          },
          { type: 'value', alignTicks: true }
        ]
        this.invoiceDailyValueData.series = []
        this.invoiceDailyValueData.series.push({
          type: 'bar',
          name: 'Account',
          stack: 'total',
          data: chartData.map(chartEntry => chartEntry.totalAccountValue),
          emphasis: {
            focus: 'series'
          },
          tooltip: {
            valueFormatter: (value) => {
              return this.formatCurrency(value);
            }
          }
        })
        this.invoiceDailyValueData.series.push({
          type: 'bar',
          name: 'Prepaid',
          stack: 'total',
          data: chartData.map(chartEntry => chartEntry.totalPrepaidValue),
          emphasis: {
            focus: 'series'
          },
          tooltip: {
            valueFormatter: (value) => {
              return this.formatCurrency(value);
            }
          }
        })
        this.invoiceDailyValueData.series.push({
          type: 'bar',
          name: 'COD',
          stack: 'total',
          data: chartData.map(chartEntry => chartEntry.totalCODValue),
          emphasis: {
            focus: 'series'
          },
          tooltip: {
            valueFormatter: (value) => {
              return this.formatCurrency(value);
            }
          }
        })
        
        this.invoiceDailyValueData.series.push({
          type: 'line',
          name: 'Invoice Count',
          yAxisIndex: 1,
          data: chartData.map(chartEntry => chartEntry.totalInvoiceCount),
        })

        this.invoiceDailyValueData.options = {
          tooltip: {
            formatter: (params) => {
              if(params.length == 0)
                return ''

              let dayTotal = params.reduce((curValue, series) => {
                if(this.invoiceDailyValueData.series[series.seriesIndex].name == 'Invoice Count')
                  return curValue
                return curValue + series.value
              }, 0)

              let lineList = params.map(series => {
                let value = series.value
                if(this.invoiceDailyValueData.series[series.seriesIndex].tooltip && this.invoiceDailyValueData.series[series.seriesIndex].tooltip.valueFormatter)
                  value = this.invoiceDailyValueData.series[series.seriesIndex].tooltip.valueFormatter(value)

                return `${series.marker}${series.seriesName}<span style="float: right; margin-left: 20px"><b>${value}</b></span>`
              })

              return `${params[0].axisValueLabel}<br />` + lineList.join('<br />') + `<br /><br />Total Invoice<span style="float: right; margin-left: 20px"><b>${this.formatCurrency(dayTotal)}</b></span>`
            }
          }
        }

        // Summary info
        this.invoiceSummaryData.averageInvoiceValue = 0
        if(chartData.length > 0) {
          this.invoiceSummaryData.averageInvoiceValue = chartData.reduce((curValue, invoiceInfo) => curValue + invoiceInfo.totalInvoiceValue, 0) / chartData.length
        }

        this.invoiceSummaryData.activeInvoiceDayCount = chartData.filter(invoiceInfo => invoiceInfo.totalInvoiceCount > 0).length
        this.invoiceSummaryData.invoiceTotalValue = invoiceSummaryData.totalInvoicedAmount
        this.invoiceSummaryData.invoiceTotalCount = invoiceSummaryData.totalInvoicedCount
        this.invoiceSummaryData.largestInvoiceInfo = invoiceSummaryData.largestInvoice        
      })
    },
  }
}
</script>